/**
 * NW Buy Box V4 — dog size + multi-dog quantity, bundle vs single pack.
 * Drives the separate "Buy buttons" block (product-form):
 *  - one line item  → sets the form's variant id + quantity, theme submit runs as normal
 *  - mixed sizes    → intercepts submit and posts several items to /cart/add.js (UpCart hooks this)
 */
(function () {
  'use strict';

  const fmt = (cents, moneyFormat) => {
    const n = Math.max(0, Math.round(Number(cents) || 0));
    const whole = n % 100 === 0;
    const amount = whole ? String(n / 100) : (n / 100).toFixed(2);
    const f = moneyFormat || '${{amount}}';
    return f
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/gi, String(Math.round(n / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/gi, amount.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/gi, amount)
      .replace(/<[^>]*>/g, '');
  };
  const fmtExact = (cents, moneyFormat) => {
    const n = Math.max(0, Math.round(Number(cents) || 0));
    const f = moneyFormat || '${{amount}}';
    return f.replace(/\{\{\s*amount\s*\}\}/gi, (n / 100).toFixed(2)).replace(/<[^>]*>/g, '');
  };

  function findForm(root) {
    const container =
      root.closest('.product__info-container') || root.closest('product-info') || document;
    const pf = container.querySelector('product-form.product-form');
    if (!pf) return { productForm: null, form: null };
    const form =
      pf.querySelector('form[data-type="add-to-cart-form"]') ||
      pf.querySelector('form[action*="cart/add"]');
    return { productForm: pf, form };
  }

  class NwBuyBoxV4 {
    constructor(root) {
      this.root = root;
      this.cfg = JSON.parse(root.querySelector('script[data-nw4-config]').textContent);
      this.hasLarge = Boolean(this.cfg.large);
      this.sub = Boolean(this.cfg.subscription);
      this.state = { qs: 1, ql: 0, pick: 'bundle', touched: false };
      this.busy = false;
    }

    init() {
      const r = this.root;
      // Dog size: one dog, small OR large.
      r.querySelectorAll('[data-nw4-pick]').forEach((b) =>
        b.addEventListener('click', () => this.setSize(b.dataset.nw4Pick))
      );
      r.querySelectorAll('[data-nw4-option]').forEach((el) => {
        el.addEventListener('click', () => this.setPick(el.dataset.nw4Option));
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.setPick(el.dataset.nw4Option);
          } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            const next = this.state.pick === 'bundle' ? 'single' : 'bundle';
            this.setPick(next);
            r.querySelector(`[data-nw4-option="${next}"]`)?.focus();
          }
        });
      });

      const { productForm, form } = findForm(r);
      this.productForm = productForm;
      this.form = form;
      if (form) {
        this.idInput = form.querySelector('input[name="id"]');
        this.qtyInput = form.querySelector('input[name="quantity"]');
        if (!this.qtyInput) {
          this.qtyInput = document.createElement('input');
          this.qtyInput.type = 'hidden';
          this.qtyInput.name = 'quantity';
          this.qtyInput.value = '1';
          form.appendChild(this.qtyInput);
        }
        this.spInput = form.querySelector('input[name="selling_plan"]');
        if (!this.spInput) {
          this.spInput = document.createElement('input');
          this.spInput.type = 'hidden';
          this.spInput.name = 'selling_plan';
          this.spInput.disabled = true;
          form.appendChild(this.spInput);
        }
        this.submitBtn = form.querySelector('[type="submit"]');
        this.btnSpan = this.submitBtn?.querySelector('span');
        this.btnBase =
          productForm.dataset.addToCartText ||
          (this.btnSpan ? this.btnSpan.textContent.trim() : '') ||
          'Add to cart';
        // Mixed sizes need several line items. Intercept on window (capture) so we run
        // before the theme's product-form AND cart apps (UpCart) that hijack the click.
        this._onClick = (e) => {
          if (!this.multi && !this.isCheckout()) return;
          const b = e.target && e.target.closest ? e.target.closest('button, input[type="submit"]') : null;
          if (!b || b !== this.submitBtn) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          this.handleBuy();
        };
        this._onSubmit = (e) => {
          if (e.target !== this.form || (!this.multi && !this.isCheckout())) return;
          e.preventDefault();
          e.stopImmediatePropagation();
          this.handleBuy();
        };
        window.addEventListener('click', this._onClick, true);
        window.addEventListener('submit', this._onSubmit, true);
        // Coming back from checkout with the browser Back button: reset the button.
        window.addEventListener('pageshow', (e) => {
          if (!e.persisted) return;
          this.busy = false;
          document.body.classList.remove('disabled-upcart');
          this.setBusy(this.submitBtn, false);
          this.setBusy(this.oneTimeBtn, false);
        });

        // Wrap buy box + Buy buttons block + note in one bordered card.
        const after = r.querySelector('[data-nw4-after]');
        const anchor = productForm.parentElement;
        if (anchor && r.parentNode && !anchor.contains(r)) {
          const card = document.createElement('div');
          card.className = `nw4-card nw4-card--${r.dataset.nw4CardAlign === 'left' ? 'left' : 'center'}`;
          const max = parseInt(r.dataset.nw4CardMax, 10);
          if (max > 0) card.style.setProperty('--nw4-card-max', `${max}px`);
          r.parentNode.insertBefore(card, r);
          card.appendChild(r);
          const oneTimeWrap = r.querySelector('[data-nw4-onetime-wrap]');
          if (oneTimeWrap) card.appendChild(oneTimeWrap);
          card.appendChild(anchor);
          if (after) card.appendChild(after);
        }
      } else if (window.Shopify && window.Shopify.designMode) {
        console.warn('[NW Buy Box V4] No Buy buttons block found in this section.');
      }

      this.oneTimeBtn = document.querySelector('[data-nw4-onetime]');
      if (this.oneTimeBtn) {
        this.oneTimeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.goCheckout({ oneTime: true });
        });
      }

      this.render();
      window.addEventListener('load', () => this.paintButton());
    }

    qty(k) { return k === 'large' ? this.state.ql : this.state.qs; }

    setSize(k) {
      if (k === 'large' && !this.hasLarge) return;
      const s = this.state;
      s.qs = k === 'large' ? 0 : 1;
      s.ql = k === 'large' ? 1 : 0;
      this.render();
    }

    inc(k) {
      if (k === 'large' && !this.hasLarge) return;
      const s = this.state;
      if (this.qty(k) >= this.cfg.maxPerSize) return;
      if (k === 'large' && this.cfg.firstLargeSwapsSmall && !s.touched && s.qs === 1 && s.ql === 0) {
        s.qs = 0;
        s.ql = 1;
      } else if (k === 'large') {
        s.ql += 1;
      } else {
        s.qs += 1;
      }
      s.touched = true;
      this.render();
    }

    dec(k) {
      const s = this.state;
      if (this.qty(k) === 0 || s.qs + s.ql <= 1) return;
      if (k === 'large') s.ql -= 1; else s.qs -= 1;
      s.touched = true;
      this.render();
    }

    setPick(p) {
      if (this.state.pick === p) return;
      this.state.pick = p;
      this.render();
    }

    /** Subscription plan for a variant: name contains the configured text (e.g. "12 week"). */
    planFor(entry, key) {
      const plans = entry && entry.plans;
      if (!plans || !plans.length) return null;
      const want = String(key === 'bundle' ? this.cfg.planMatchBundle : this.cfg.planMatchSingle || '')
        .trim().toLowerCase();
      return plans.find((p) => want && String(p.name).toLowerCase().includes(want)) || plans[0];
    }

    /** Price per pack/bundle: subscription price when in subscription mode. */
    priceOf(entry, key, useSub) {
      if (!entry) return 0;
      if (useSub) {
        const plan = this.planFor(entry, key);
        if (plan) return plan.price;
      }
      return entry.price;
    }

    lines(opts) {
      const useSub = opts && opts.oneTime ? false : this.sub;
      const s = this.state;
      const key = s.pick === 'bundle' ? 'bundle' : 'single';
      const out = [];
      const add = (entry, qty) => {
        const line = { id: entry.id, quantity: qty };
        if (useSub) {
          const plan = this.planFor(entry, key);
          if (plan) line.selling_plan = plan.id;
        }
        out.push(line);
      };
      if (s.qs > 0) add(this.cfg.small[key], s.qs);
      if (s.ql > 0 && this.hasLarge) add(this.cfg.large[key], s.ql);
      return out;
    }

    numbers() {
      const { qs, ql } = this.state;
      const c = this.cfg;
      const L = this.hasLarge ? c.large : { single: { price: 0 }, bundle: { price: 0 } };
      const n = qs + ql;
      const sub = this.sub;
      const single = qs * this.priceOf(c.small.single, 'single', sub) + ql * this.priceOf(L.single, 'single', sub);
      const bundle = qs * this.priceOf(c.small.bundle, 'bundle', sub) + ql * this.priceOf(L.bundle, 'bundle', sub);
      // "Was" price is always the regular one-time monthly price x months.
      const singleOneTime = qs * c.small.single.price + ql * L.single.price;
      const bundleOneTime = qs * c.small.bundle.price + ql * L.bundle.price;
      const retail = singleOneTime * c.months;
      const save = Math.max(0, retail - bundle);
      return {
        n,
        single,
        bundle,
        singleOneTime,
        bundleOneTime,
        retail,
        save,
        pct: retail > 0 ? Math.round((save / retail) * 100) : 0,
        bMonth: Math.round(bundle / c.months),
        sStick: Math.round(single / (c.sticksPerPack * n)),
        bStick: Math.round(bundle / (c.sticksPerPack * c.months * n)),
      };
    }

    setAll(sel, text) {
      this.root.querySelectorAll(sel).forEach((el) => { el.textContent = text; });
    }

    render() {
      const r = this.root;
      const s = this.state;
      const c = this.cfg;
      const mf = c.moneyFormat;
      const x = this.numbers();
      const dogs = x.n === 1 ? '' : ` for ${x.n} dogs`;

      // Size tiles
      ['small', 'large'].forEach((k) => {
        const tile = r.querySelector(`[data-nw4-size="${k}"]`);
        if (!tile) return;
        const on = this.qty(k) > 0;
        tile.classList.toggle('is-on', on);
        tile.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      // Options
      r.querySelectorAll('[data-nw4-option]').forEach((el) => {
        const on = el.dataset.nw4Option === s.pick;
        el.classList.toggle('is-selected', on);
        el.setAttribute('aria-checked', on ? 'true' : 'false');
        el.tabIndex = on ? 0 : -1;
      });

      // Copy + prices
      this.setAll('[data-nw4-bundle-title]',
        x.n === 1 ? c.copy.bundleTitleOne : `${c.months * x.n} packs · ${c.months} months${dogs}`);
      this.setAll('[data-nw4-bundle-sub]',
        x.n === 1 ? c.copy.bundleSubOne : `Pay for ${(c.months - 1) * x.n}, get ${x.n} packs free`);
      this.setAll('[data-nw4-single-title]',
        x.n === 1 ? c.copy.singleTitleOne : `${x.n} packs · 1 month${dogs}`);
      this.setAll('[data-nw4-bundle-month]', fmt(x.bMonth, mf));
      this.setAll('[data-nw4-single-month]', fmt(x.single, mf));
      this.setAll('[data-nw4-save]', fmt(x.save, mf));
      this.setAll('[data-nw4-bundle-total]', fmt(x.bundle, mf));
      this.setAll('[data-nw4-pct]', `${x.pct}%`);
      this.setAll('[data-nw4-bundle-stick]', fmtExact(x.bStick, mf));
      this.setAll('[data-nw4-single-stick]', fmtExact(x.sStick, mf));
      this.setAll('[data-nw4-single-stick-plain]', fmtExact(x.sStick, mf));

      // Note under the button
      const note = document.querySelector('[data-nw4-note]');
      if (note) {
        const tpl = s.pick === 'bundle' ? c.copy.bundleNote : c.copy.singleNote;
        const safe = (tpl || '').replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));
        note.innerHTML = safe.replace(/\[save\]/g, `<b>${fmt(x.save, mf)}</b>`);
      }

      if (this.oneTimeBtn) {
        const ot = s.pick === 'bundle' ? x.bundleOneTime : x.singleOneTime;
        this.oneTimeBtn.textContent = (c.oneTimeLinkText || 'One-time purchase').replace(/\[price\]/g, fmt(ot, mf));
      }

      this.syncForm(x);
    }

    syncForm(x) {
      if (!this.form) return;
      const lines = this.lines();
      this.multi = lines.length > 1;

      if (this.idInput && lines[0]) {
        if (String(this.idInput.value) !== String(lines[0].id)) {
          this.idInput.value = lines[0].id;
          this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.qtyInput.value = String(lines[0].quantity);
      }
      if (this.spInput) {
        const sp = !this.multi && lines[0] && lines[0].selling_plan;
        this.spInput.value = sp ? String(sp) : '';
        this.spInput.disabled = !sp;
      }

      // Fallback for anything that submits the form itself (e.g. a cart app):
      // with mixed sizes, send items[] instead of a single id so both lines are added.
      this.form.querySelectorAll('input[data-nw4-item]').forEach((el) => el.remove());
      if (this.idInput) this.idInput.disabled = this.multi;
      this.qtyInput.disabled = this.multi;
      if (this.multi) {
        lines.forEach((l, i) => {
          const fields = [['id', l.id], ['quantity', l.quantity]];
          if (l.selling_plan) fields.push(['selling_plan', l.selling_plan]);
          fields.forEach(([k, v]) => {
            const inp = document.createElement('input');
            inp.type = 'hidden';
            inp.name = `items[${i}][${k}]`;
            inp.value = String(v);
            inp.setAttribute('data-nw4-item', '');
            this.form.appendChild(inp);
          });
        });
      }

      const variants = lines.map((l) => {
        const k = this.state.pick === 'bundle' ? 'bundle' : 'single';
        return [this.cfg.small[k], this.hasLarge ? this.cfg.large[k] : null].find((v) => v && v.id === l.id);
      });
      const available = variants.every((v) => v && v.available);
      if (this.submitBtn) {
        this.submitBtn.disabled = !available;
      }

      this._btnState = available ? x : null;
      this.paintButton();
    }

    /** Price (and struck-through full price) inside the Add to cart button. */
    paintButton() {
      const x = this._btnState;
      if (!this.submitBtn || !this.cfg.showPriceInButton || !x) return;
      const span = this.submitBtn.querySelector(':scope > span:not(.loading__spinner)') || this.btnSpan;
      if (!span) return;
      this.btnSpan = span;
      const mf = this.cfg.moneyFormat;
      const bundle = this.state.pick === 'bundle';
      let main;
      let was = '';
      if (this.cfg.buttonPriceMode === 'monthly') {
        // Per-month price, e.g. "$26/mo" with "$39/mo" struck through.
        const perMonth = bundle ? x.bMonth : x.single;
        main = `${this.btnBase} · ${fmt(perMonth, mf)}/mo`;
        if (bundle && x.single > x.bMonth) was = `${fmt(x.single, mf)}/mo`;
      } else {
        // Total charged today, e.g. "$93" with "$117" struck through.
        main = `${this.btnBase} · ${fmt(bundle ? x.bundle : x.single, mf)}`;
        if (bundle && x.retail > x.bundle) was = fmt(x.retail, mf);
      }
      this._btnExpected = main + was;
      if (span.textContent === this._btnExpected && (!was || span.querySelector('.nw4-btn-was'))) return;

      span.textContent = main;
      if (was) {
        const s = document.createElement('span');
        s.className = 'nw4-btn-was';
        s.textContent = was;
        s.style.cssText = 'text-decoration:line-through;text-decoration-thickness:2px;opacity:.75;margin-left:8px;font-weight:400;display:inline;';
        span.appendChild(s);
      }

      // If the theme or an app rewrites the button text later, put the prices back.
      if (!this._btnObserver) {
        this._btnObserver = new MutationObserver(() => {
          const cur = this.submitBtn.querySelector(':scope > span:not(.loading__spinner)');
          if (cur && cur.textContent !== this._btnExpected) this.paintButton();
        });
        this._btnObserver.observe(this.submitBtn, { childList: true, subtree: true, characterData: true });
      }
    }

    isCheckout() {
      return this.productForm?.dataset.buttonAction === 'checkout';
    }

    handleBuy() {
      if (this.isCheckout()) this.goCheckout();
      else this.addMulti();
    }

    /** fetch() from a blank same-origin iframe, so cart-drawer apps that hook fetch don't react. */
    cleanFetch(url, init) {
      try {
        if (!this._cleanFetch) {
          const f = document.createElement('iframe');
          f.style.display = 'none';
          f.setAttribute('aria-hidden', 'true');
          document.body.appendChild(f);
          this._cleanFetch = f.contentWindow.fetch.bind(f.contentWindow);
        }
        return this._cleanFetch(window.location.origin + url, init);
      } catch (e) {
        return fetch(url, init);
      }
    }

    setBusy(el, on) {
      if (!el) return;
      el.classList.toggle('loading', on);
      if (on) el.setAttribute('aria-disabled', 'true'); else el.removeAttribute('aria-disabled');
      if (el === this.submitBtn) {
        this.productForm?.querySelector('.loading__spinner')?.classList.toggle('hidden', !on);
      }
    }

    /**
     * Direct-to-checkout with exactly the V4 selection (anything already in the cart is replaced).
     *  - one-time: cart permalink, no cart requests at all
     *  - subscription: clear + add (with selling plans) quietly, then go to /checkout
     */
    goCheckout(opts) {
      const oneTime = Boolean(opts && opts.oneTime);
      const el = oneTime ? this.oneTimeBtn : this.submitBtn;
      if (this.busy || this.submitBtn?.disabled) return;
      this.busy = true;
      document.body.classList.add('disabled-upcart');
      this.setBusy(el, true);

      const lines = this.lines({ oneTime });
      const tag = this.form.querySelector('input[name="properties[_order_tag]"]')?.value;

      if (!lines.some((l) => l.selling_plan)) {
        const path = lines.map((l) => `${l.id}:${l.quantity}`).join(',');
        const params = new URLSearchParams();
        if (tag) params.set('attributes[_order_tag]', tag);
        const qs = params.toString();
        window.location.assign(`/cart/${path}${qs ? `?${qs}` : ''}`);
        return;
      }

      const items = lines.map((l) => (tag ? { ...l, properties: { _order_tag: tag } } : l));
      const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
      this.cleanFetch('/cart/clear.js', { method: 'POST', headers: json, credentials: 'same-origin' })
        .then(() =>
          this.cleanFetch('/cart/add.js', {
            method: 'POST',
            headers: json,
            credentials: 'same-origin',
            body: JSON.stringify({ items }),
          })
        )
        .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
        .then(({ ok, data }) => {
          if (!ok || data.status) throw new Error(data.description || data.message || 'Could not start checkout');
          window.location.assign('/checkout');
        })
        .catch((err) => {
          console.error('[NW Buy Box V4]', err);
          this.busy = false;
          document.body.classList.remove('disabled-upcart');
          this.setBusy(el, false);
          const wrap = this.productForm?.querySelector('.product-form__error-message-wrapper');
          const msg = wrap?.querySelector('.product-form__error-message');
          if (wrap && msg) { msg.textContent = err.message; wrap.hidden = false; }
        });
    }

    addMulti() {
      if (this.busy) return;
      this.busy = true;

      const tag = this.form.querySelector('input[name="properties[_order_tag]"]')?.value;
      const items = this.lines().map((l) => (tag ? { ...l, properties: { _order_tag: tag } } : l));
      const btn = this.submitBtn;
      btn?.classList.add('loading');
      btn?.setAttribute('aria-disabled', 'true');
      this.productForm?.querySelector('.loading__spinner')?.classList.remove('hidden');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      })
        .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
        .then(({ ok, data }) => {
          if (!ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'nw-buy-box-v4', cartData: data });
          }
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          if (typeof window.upcartRefreshCart === 'function') {
            window.upcartRefreshCart();
            if (typeof window.upcartOpenCart === 'function') window.upcartOpenCart();
          } else if (!document.querySelector('cart-drawer')) {
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
          }
        })
        .catch((err) => {
          console.error('[NW Buy Box V4]', err);
          const wrap = this.productForm?.querySelector('.product-form__error-message-wrapper');
          const msg = wrap?.querySelector('.product-form__error-message');
          if (wrap && msg) { msg.textContent = err.message; wrap.hidden = false; }
        })
        .finally(() => {
          this.busy = false;
          btn?.classList.remove('loading');
          btn?.removeAttribute('aria-disabled');
          this.productForm?.querySelector('.loading__spinner')?.classList.add('hidden');
        });
    }
  }

  function boot() {
    document.querySelectorAll('[data-nw4-root]').forEach((root) => {
      if (root._nw4) return;
      try {
        root._nw4 = new NwBuyBoxV4(root);
        root._nw4.init();
      } catch (err) {
        console.error('[NW Buy Box V4] init failed', err);
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
